import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Sparkles, 
  Calendar, 
  ShieldCheck, 
  AlertCircle 
} from 'lucide-react';
import API from '../api_config';
import { useAuth } from '../contexts/AuthContext';

export default function AgentMemorySettings() {
  const { user, token } = useAuth();
  const [learnings, setLearnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editFact, setEditFact] = useState('');
  const [editCategory, setEditCategory] = useState('general');

  useEffect(() => {
    if (user && token) {
      fetchLearnings();
    }
  }, [user, token]);

  const fetchLearnings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/agent/learnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearnings(response.data);
    } catch (error) {
      console.error('Error al cargar memoria del agente:', error);
      toast.error('No se pudo cargar la memoria del agente');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApproved = async (id, currentStatus) => {
    try {
      const response = await axios.put(`${API}/agent/learnings/${id}`, {
        is_approved: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLearnings(learnings.map(l => l.id === id ? response.data : l));
      toast.success(
        !currentStatus 
          ? 'Regla activada. El agente la usará en sus respuestas.' 
          : 'Regla pausada. El agente la ignorará.'
      );
    } catch (error) {
      toast.error('Error al actualizar el estado del aprendizaje');
    }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditFact(item.fact);
    setEditCategory(item.category);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFact('');
  };

  const handleSaveEdit = async (id) => {
    if (!editFact.trim()) return toast.error('El hecho no puede estar vacío');
    try {
      const response = await axios.put(`${API}/agent/learnings/${id}`, {
        fact: editFact,
        category: editCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLearnings(learnings.map(l => l.id === id ? response.data : l));
      setEditingId(null);
      toast.success('Regla de memoria editada correctamente');
    } catch (error) {
      toast.error('Error al guardar los cambios');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este aprendizaje permanentemente?')) return;
    try {
      await axios.delete(`${API}/agent/learnings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearnings(learnings.filter(l => l.id !== id));
      toast.success('Regla de memoria eliminada correctamente');
    } catch (error) {
      toast.error('Error al eliminar la regla');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-5xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <Brain className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            Memoria del Agente (Reglas de la Empresa)
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Administra los hechos, políticas y preferencias locales de tu negocio que el agente inteligente ha aprendido.
          </p>
        </div>
        <div className="bg-indigo-50 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300 px-4 py-2 rounded-xl border border-indigo-100 dark:border-indigo-900/50 flex items-center gap-2 text-xs font-semibold">
          <Sparkles className="w-4 h-4 animate-pulse" />
          Aprendizaje Continuo Habilitado
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="border-zinc-200 dark:border-zinc-800 shadow-md">
          <CardHeader className="bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800">
            <CardTitle className="text-base font-semibold">Reglas y Hechos Registrados</CardTitle>
            <CardDescription>
              El agente utiliza las reglas activas de esta lista para personalizar sus respuestas de ayuda a tus colaboradores.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {learnings.length === 0 ? (
              <div className="text-center py-16 text-zinc-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-semibold text-sm">Aún no hay hechos aprendidos</p>
                <p className="text-xs max-w-sm mx-auto mt-1 text-zinc-500">
                  El agente inteligente registrará datos automáticamente en esta lista a medida que conversen con él sobre el negocio.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {learnings.map((item) => (
                  <div 
                    key={item.id}
                    className="p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/10 transition-colors"
                  >
                    <div className="flex-1 space-y-3">
                      {editingId === item.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={editFact}
                            onChange={(e) => setEditFact(e.target.value)}
                            className="w-full bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 text-zinc-800 dark:text-zinc-100"
                            rows={3}
                          />
                          <div className="flex gap-4 items-center">
                            <label className="text-xs font-semibold text-zinc-500">Categoría:</label>
                            <select
                              value={editCategory}
                              onChange={(e) => setEditCategory(e.target.value)}
                              className="bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 text-zinc-800 dark:text-zinc-100"
                            >
                              <option value="general">General</option>
                              <option value="business_hours">Horario de Atención</option>
                              <option value="inventory_preference">Preferencia de Inventario</option>
                              <option value="credit_policy">Política de Crédito</option>
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 bg-zinc-50 dark:bg-zinc-900/60 p-3 rounded-xl border border-zinc-100 dark:border-zinc-800 italic">
                            "{item.fact}"
                          </p>
                          <div className="flex flex-wrap items-center gap-3 text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${
                              item.category === 'business_hours' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300' :
                              item.category === 'inventory_preference' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300' :
                              item.category === 'credit_policy' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' :
                              'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300'
                            }`}>
                              {item.category === 'business_hours' ? 'Horario' :
                               item.category === 'inventory_preference' ? 'Inventario' :
                               item.category === 'credit_policy' ? 'Créditos' : 'General'}
                            </span>
                            
                            <span className="text-zinc-400 flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(item.created_at).toLocaleDateString('es-CL')}
                            </span>
                            
                            <span className="text-zinc-400 flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5" />
                              Confianza: {Math.round(item.confidence * 100)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                      {editingId === item.id ? (
                        <>
                          <Button 
                            onClick={() => handleSaveEdit(item.id)}
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5"
                          >
                            <Check className="w-4 h-4" /> Guardar
                          </Button>
                          <Button 
                            onClick={handleCancelEdit}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1.5"
                          >
                            <X className="w-4 h-4" /> Cancelar
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleToggleApproved(item.id, item.is_approved)}
                            variant={item.is_approved ? "default" : "outline"}
                            size="sm"
                            className={`min-w-[90px] text-xs font-semibold ${
                              item.is_approved 
                                ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' 
                                : 'border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                            }`}
                          >
                            {item.is_approved ? 'Activo' : 'Pausado'}
                          </Button>
                          <Button
                            onClick={() => handleStartEdit(item)}
                            variant="outline"
                            size="icon"
                            className="border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                          >
                            <Edit2 className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(item.id)}
                            variant="destructive"
                            size="icon"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
